import React, { createContext, useContext, useState } from "react";
import { TriangleAlert, SquareCheckBig, X, Info } from "lucide-react";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now();
    const newNotification = { ...notification, id };
    setNotifications((prev) => [...prev, newNotification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);

    return id;
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const success = (message) => addNotification({ type: "success", message });
  const error = (message) => addNotification({ type: "error", message });
  const info = (message) => addNotification({ type: "info", message });
  const warning = (message) => addNotification({ type: "warning", message });

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        success,
        error,
        info,
        warning,
      }}
    >
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = ({ notifications, onRemove }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {notifications.map((notification) => (
        <NotificationCard key={notification.id} notification={notification} onRemove={onRemove} />
      ))}
    </div>
  );
};

const NotificationCard = ({ notification, onRemove }) => {
  const { type, message, id } = notification;

  const typeStyles = {
    success: "bg-green-100 border-green-400 text-green-700",
    error: "bg-red-100 border-red-400 text-red-700",
    warning: "bg-yellow-100 border-yellow-400 text-yellow-700",
    info: "bg-blue-100 border-blue-400 text-blue-700",
  };

  const icons = {
    success: <SquareCheckBig />,
    error: <X />,
    warning: <TriangleAlert />,
    info: <Info />,
  };

  return (
    <div className={`${typeStyles[type]} border px-4 py-3 rounded-lg shadow-lg max-w-md animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{icons[type]}</span>
          <span className="font-medium">{message}</span>
        </div>
        <button onClick={() => onRemove(id)} className="text-gray-500 hover:text-gray-700 ml-4">
          ✕
        </button>
      </div>
    </div>
  );
};
